import {DestroyRef, Directive, inject, OnInit, ViewChild} from '@angular/core';
import {
  AbstractObjectDetailService,
  CodebookService,
  DocumentDtoWithAuthorization,
  DocumentFileCustomFieldsSectionComponent,
  DocumentFileDetailService, DocumentSearchService,
  FileDtoWithAuthorization,
  FORM_SUBMIT_DEBOUNCE_TIME,
  GeneralAuthorizationResult,
  ObjectDetailSubmitActionType,
} from '|shared';
import {debounceTime, map, switchMap} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ApiDocumentFileBulkActionsService,
  CaseFileDto,
  FileDto,
  RegistryOfficeTransferWithMainEntityInfoDto,
  StorageUnitDto
} from '|api/document';
import {Router} from '@angular/router';
import {ApiSubjectRecordElasticService} from '|api/subject-register';
import {IczFormGroup} from '@icz/angular-form-elements';
import {TranslateService} from '@ngx-translate/core';
import {EntityType, SettlementType} from '|api/commons';
import {CaseFileElasticDto, SearchRecordSourceDocumentFileDto} from '|api/elastic';
import {FilterOperator} from '@icz/angular-table';
import { CheckUnsavedFormPageService, IFormGroupCheckable } from '@icz/angular-application-core';
import {Observable, of} from 'rxjs';
import {DialogService} from '@icz/angular-modal';

@Directive()
export abstract class AbstractObjectDetailPreviewComponent implements OnInit, IFormGroupCheckable {

  protected abstractObjectDetailService = inject(AbstractObjectDetailService);
  protected destroyRef = inject(DestroyRef);
  private checkUnsavedService = inject(CheckUnsavedFormPageService);

  abstract form: IczFormGroup;

  authorization!: GeneralAuthorizationResult;
  hideSubmitBarTimeout: Nullable<number>;

  registryOfficeTransfer: Nullable<RegistryOfficeTransferWithMainEntityInfoDto>;

  formGroupsToCheck!: string[];

  get isInRegistryOffice() {
    return Boolean(this.registryOfficeTransfer);
  }

  ngOnInit() {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);

    this.abstractObjectDetailService.submit$.pipe(
      debounceTime(FORM_SUBMIT_DEBOUNCE_TIME),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(submitActionType => {
      this.onSubmit(submitActionType);
    });

    this.abstractObjectDetailService.cancel$.pipe(
      debounceTime(FORM_SUBMIT_DEBOUNCE_TIME),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.onCancel());

    this.abstractObjectDetailService.object$.pipe(
      switchMap(object => this.abstractObjectDetailService.registryOfficeTransfer$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(registryOfficeTransfer => {
      this.registryOfficeTransfer = registryOfficeTransfer;
    });
  }

  abstract onSubmit(submitActionType: ObjectDetailSubmitActionType): void;

  abstract onCancel(): void;

}

@Directive()
export abstract class DocumentFileDetailPreviewComponent<TPreviewDto extends DocumentDtoWithAuthorization | FileDtoWithAuthorization | CaseFileDto> extends AbstractObjectDetailPreviewComponent {

  protected override abstractObjectDetailService = inject(DocumentFileDetailService);
  protected router = inject(Router);
  protected documentSearchService = inject(DocumentSearchService);
  protected apiSubjectRecordNgElasticService = inject(ApiSubjectRecordElasticService);
  protected dialogService = inject(DialogService);
  private apiDocumentFileBulkActionsService = inject(ApiDocumentFileBulkActionsService);

  @ViewChild(DocumentFileCustomFieldsSectionComponent)
  customFieldsSection!: DocumentFileCustomFieldsSectionComponent;

  storageUnit: Nullable<StorageUnitDto>;
  caseFileInfo: Nullable<CaseFileElasticDto>;
  isStorageUnitUnavailable = false;
  isCaseFileInfoUnavailable = false;
  otherSettlementMethod: Nullable<string>;

  representingSubject$ = this.abstractObjectDetailService.objectRepresentingSubject$;

  fillOtherSettlementMethod(codebookService: CodebookService, translateService: TranslateService, settlementMethodId: Nullable<number>) {
    if (settlementMethodId) {
      codebookService.otherSettlementMethods().subscribe(methods => {
        const selectedMethod = methods.find(method => method.id === settlementMethodId);
        if (selectedMethod) {
          this.otherSettlementMethod = selectedMethod.name;
        } else {
          this.otherSettlementMethod = translateService.instant(`en.settlementType.${SettlementType.BY_OTHER_METHOD}`);
        }
      });
    } else {
      this.otherSettlementMethod = translateService.instant(`en.settlementType.${SettlementType.BY_OTHER_METHOD}`);
    }
  }

  checkCrossReferences(documentIds: number[], fileIds: number[]): Observable<boolean> {
    return this.apiDocumentFileBulkActionsService.documentFileBulkActionsCrossReferenceCheck({body: {documentIds, fileIds}})
      .pipe(switchMap(crossCheck => {
        const crossReferencesToUpdateExist = documentIds.length ? crossCheck.documentIds.includes(documentIds[0]!) :
          crossCheck.fileIds.includes(fileIds[0]!);
        if (crossReferencesToUpdateExist) {
          return this.dialogService.openQuestionDialogWithAnswer(
            {
              title: 'Aktualizace navázaných objektů',
              question: 'Přejete si aktualizovat i navázané objekty?',
              description: 'Aktualizovaný dokument má existující křížové vazby, jež by se mohly také aktualizovat.',
              leftButtonTitle: 'Aktualizovat'
            }
          );
        }
        else return of(false);
      }));
  }

  onCancel() {
    this.abstractObjectDetailService.resetActiveForm();
    this.customFieldsSection.computeAvailableFields();
  }

  protected getCaseFile(file: FileDto) {
    if (!file.caseFile) return;

    const caseFileInfo$ = this.documentSearchService.findDocumentsAndFilesWithPermissions({
      filter: [
        {
          fieldName: 'entityType',
          operator: FilterOperator.equals,
          value: EntityType.FILE
        },
        {
          fieldName: 'id',
          operator: FilterOperator.equals,
          value: String(file.id),
        }
      ]
    }).pipe(
      map(page => (page.content[0] as unknown as Nullable<SearchRecordSourceDocumentFileDto>)?.caseFile),
    );

    if (caseFileInfo$) {
      caseFileInfo$.subscribe({
        next: caseFileInfo => {
          this.isCaseFileInfoUnavailable = false;
          this.caseFileInfo = caseFileInfo;
        },
        error: (_: any) => {
          this.isCaseFileInfoUnavailable = true;
        }
      });
    }
    else {
      this.isCaseFileInfoUnavailable = true;
    }
  }

  protected getStorageUnit(object: TPreviewDto) {
    const storageUnit$ = this.abstractObjectDetailService.getStorageUnit(object);

    if (storageUnit$) {
      storageUnit$.subscribe(storageUnit => {
        this.isStorageUnitUnavailable = false;
        this.storageUnit = storageUnit;
      });
    }
    else {
      this.isStorageUnitUnavailable = true;
    }
  }

}
